<template>
  <div class="wl-reports-overview">
    <b-card class="mb-3">
      <b-row class="wl-filters align-items-start">
        <b-col cols="12" md="4">
          <b-form-group label="Facility" label-class="font-weight-bold mb-1" class="mb-md-0">
            <treeselect
              v-model="filters.facilities_id"
              :options="facilityOptions"
              :normalizer="facilityNormalizer"
              :multiple="true"
              :clearable="true"
              placeholder="All facilities"
              :disabled="facilityOptionsLoading" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="From" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_from" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="2">
          <b-form-group label="To" label-class="font-weight-bold mb-1" class="mb-md-0">
            <b-form-input v-model="filters.date_to" type="date" />
          </b-form-group>
        </b-col>
        <b-col cols="12" md="4" class="d-flex justify-content-end pb-1">
          <b-button
            variant="primary"
            :disabled="loading || facilityOptionsLoading"
            @click="loadAll">
            <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loading }"></i>
            Refresh dashboard
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <b-alert v-if="loadError" show variant="danger" class="mb-3">{{ loadError }}</b-alert>

    <b-row v-if="loading && !hasData" class="mb-3">
      <b-col cols="12" class="text-center py-5 text-muted">
        <b-spinner class="mr-2" />
        Loading reports...
      </b-col>
    </b-row>

    <template v-if="hasData">
      <b-row class="mb-3">
        <b-col
          v-for="kpi in kpiCards"
          :key="kpi.key"
          cols="6"
          md="4"
          lg="2"
          class="mb-3 mb-lg-0">
          <b-card class="wl-kpi-card h-100 text-center border-0 shadow-sm">
            <div class="wl-kpi-icon text-muted mb-2">
              <i :class="kpi.icon"></i>
            </div>
            <h3 class="mb-0 font-weight-bolder">{{ kpi.value }}</h3>
            <small class="text-muted d-block">{{ kpi.label }}</small>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col cols="12" lg="4" class="mb-3 mb-lg-0">
          <b-card class="shadow-sm border-0 h-100">
            <h6 class="font-weight-bold mb-3">Stock status</h6>
            <apexchart
              v-if="stockStatusSeries.length"
              type="donut"
              height="300"
              :options="stockStatusChartOptions"
              :series="stockStatusSeries" />
            <p v-else class="text-muted small mb-0">No stock data</p>
          </b-card>
        </b-col>
        <b-col cols="12" lg="4" class="mb-3 mb-lg-0">
          <b-card class="shadow-sm border-0 h-100">
            <h6 class="font-weight-bold mb-3">Movements by type</h6>
            <apexchart
              v-if="movementTypeSeries.length"
              type="pie"
              height="300"
              :options="movementTypeChartOptions"
              :series="movementTypeSeries" />
            <p v-else class="text-muted small mb-0">No movements in range</p>
          </b-card>
        </b-col>
        <b-col cols="12" lg="4">
          <b-card class="shadow-sm border-0 h-100">
            <h6 class="font-weight-bold mb-3">Visits — injection</h6>
            <apexchart
              v-if="injectionSeries.length"
              type="donut"
              height="300"
              :options="injectionChartOptions"
              :series="injectionSeries" />
            <p v-else class="text-muted small mb-0">No visits in range</p>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col cols="12" lg="8" class="mb-3 mb-lg-0">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-3">Top stock levels (units)</h6>
            <apexchart
              v-if="topStockCategories.length"
              type="bar"
              height="320"
              :options="topStockChartOptions"
              :series="topStockSeries" />
            <p v-else class="text-muted small mb-0">No items</p>
          </b-card>
        </b-col>
        <b-col cols="12" lg="4">
          <b-card class="shadow-sm border-0 h-100">
            <h6 class="font-weight-bold mb-3 text-danger">Below minimum</h6>
            <apexchart
              v-if="lowStockCategories.length"
              type="bar"
              height="320"
              :options="lowStockChartOptions"
              :series="lowStockSeries" />
            <p v-else class="text-success small mb-0">No low / critical / empty items</p>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-3">Visits over time</h6>
            <apexchart
              v-if="visitsTimelineCategories.length"
              type="area"
              height="300"
              :options="visitsTimelineOptions"
              :series="visitsTimelineSeries" />
            <p v-else class="text-muted small mb-0">No visits in range</p>
          </b-card>
        </b-col>
        <b-col cols="12" lg="6">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-3">Revenue charged ($)</h6>
            <apexchart
              v-if="revenueTimelineCategories.length"
              type="line"
              height="300"
              :options="revenueTimelineOptions"
              :series="revenueTimelineSeries" />
            <p v-else class="text-muted small mb-0">No billing data</p>
          </b-card>
        </b-col>
      </b-row>

      <b-row class="mb-3">
        <b-col cols="12" lg="6" class="mb-3 mb-lg-0">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-3">Movement quantity by type</h6>
            <apexchart
              v-if="movementQtyCategories.length"
              type="bar"
              height="280"
              :options="movementQtyChartOptions"
              :series="movementQtySeries" />
            <p v-else class="text-muted small mb-0">—</p>
          </b-card>
        </b-col>
        <b-col cols="12" lg="6">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-3">Active treatments</h6>
            <apexchart
              v-if="treatmentBarCategories.length"
              type="bar"
              height="280"
              :options="treatmentChartOptions"
              :series="treatmentBarSeries" />
            <p v-else class="text-muted small mb-0">No treatments</p>
          </b-card>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12" md="6" class="mb-3">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-2">Recent visits</h6>
            <b-table
              :items="recentVisits"
              :fields="recentVisitFields"
              small
              bordered
              responsive
              show-empty
              empty-text="—" />
          </b-card>
        </b-col>
        <b-col cols="12" md="6" class="mb-3">
          <b-card class="shadow-sm border-0">
            <h6 class="font-weight-bold mb-2">Stock alerts</h6>
            <b-table
              :items="lowStockTable"
              :fields="lowStockFields"
              small
              bordered
              responsive
              show-empty
              empty-text="All OK">
              <template #cell(stock_status)="data">
                <b-badge :variant="statusVariant(data.value)">{{ statusLabel(data.value) }}</b-badge>
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>
    </template>
  </div>
</template>

<script>
import { BCard, BRow, BCol, BButton, BFormGroup, BFormInput, BAlert, BTable, BBadge, BSpinner } from 'bootstrap-vue'
import Treeselect from '@riophae/vue-treeselect'
import VueApexCharts from 'vue-apexcharts'
import wlTreatmentsApi from '@/core/services/api/weightloss-inventory/treatments'
import wlPricesApi from '@/core/services/api/weightloss-inventory/prices'
import invMovementsApi from '@/core/services/api/weightloss-inventory/movements'
import invStockApi from '@/core/services/api/weightloss-inventory/stock'
import invItemsApi from '@/core/services/api/weightloss-inventory/items'
import { facilitiesFilterParams } from '@/core/services/api/weightloss-inventory/facility'
import axiosF from '@/core/services/api/admin/facilities'
import wlLaneMixin from '../mixins/wlLaneMixin'

const CHART_COLORS = ['#3699FF', '#1BC5BD', '#FFA800', '#F64E60', '#8950FC', '#6993FF']
const STATUS_COLORS = { ok: '#1BC5BD', low: '#FFA800', critical: '#F64E60', empty: '#B5B5C3' }

const TYPE_LABELS = {
  in: 'In',
  out: 'Out',
  adjustment_in: 'Adj. in',
  adjustment_out: 'Adj. out',
  transfer_in: 'Transfer in',
  transfer_out: 'Transfer out',
}

export default {
  name: 'WlReportsOverview',
  mixins: [wlLaneMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormInput,
    BAlert,
    BTable,
    BBadge,
    BSpinner,
    Treeselect,
    apexchart: VueApexCharts,
  },
  data() {
    return {
      loading: false,
      loadError: '',
      facilityOptionsLoading: false,
      facilityOptions: [],
      facilityNormalizer(node) {
        return { id: node.id, label: node.name }
      },
      filters: {
        // All facilities by default (omit facilities_id)
        facilities_id: [],
        date_from: '',
        date_to: '',
      },
      treatments: [],
      patientsTotal: 0,
      visits: [],
      movements: [],
      stock: [],
      prices: [],
      itemsTotal: 0,
      recentVisitFields: [
        { key: 'visit_date', label: 'Date' },
        { key: 'patient', label: 'Patient' },
        { key: 'price_charged', label: 'Charged' },
      ],
      lowStockFields: [
        { key: 'name', label: 'Item' },
        { key: 'stock', label: 'Stock' },
        { key: 'stock_status', label: 'Status' },
      ],
    }
  },
  computed: {
    hasData() {
      return !this.loading
    },
    baseParams() {
      const p = {
        ...facilitiesFilterParams(this.filters.facilities_id),
      }
      if (this.filters.date_from) p.date_from = this.filters.date_from
      if (this.filters.date_to) p.date_to = this.filters.date_to
      return p
    },
    stockAlerts() {
      return this.stock.filter(s => ['low', 'critical', 'empty'].includes(s.stock_status))
    },
    totalRevenue() {
      return this.visits.reduce((sum, v) => sum + (Number(v.price_charged) || 0), 0)
    },
    injectionsCount() {
      return this.visits.filter(v => v.injection_given).length
    },
    kpiCards() {
      return [
        { key: 'treatments', label: 'Treatments', value: this.treatments.length, icon: 'fas fa-syringe' },
        { key: 'patients', label: 'Patients', value: this.patientsTotal, icon: 'fas fa-user-injured' },
        { key: 'visits', label: 'Visits', value: this.visits.length, icon: 'fas fa-calendar-check' },
        { key: 'movements', label: 'Movements', value: this.movements.length, icon: 'fas fa-exchange-alt' },
        { key: 'alerts', label: 'Stock alerts', value: this.stockAlerts.length, icon: 'fas fa-exclamation-triangle' },
        { key: 'revenue', label: 'Revenue ($)', value: this.formatMoney(this.totalRevenue), icon: 'fas fa-dollar-sign' },
      ]
    },
    stockStatusSeries() {
      const counts = { ok: 0, low: 0, critical: 0, empty: 0 }
      this.stock.forEach(s => {
        const k = s.stock_status || 'ok'
        if (counts[k] !== undefined) counts[k] += 1
        else counts.ok += 1
      })
      return Object.values(counts)
    },
    stockStatusChartOptions() {
      return this.donutOptions(['OK', 'Low', 'Critical', 'Empty'], [
        STATUS_COLORS.ok,
        STATUS_COLORS.low,
        STATUS_COLORS.critical,
        STATUS_COLORS.empty,
      ])
    },
    movementTypeMap() {
      const counts = {}
      this.movements.forEach(m => {
        const t = m.type || 'other'
        counts[t] = (counts[t] || 0) + 1
      })
      return counts
    },
    movementTypeSeries() {
      return Object.values(this.movementTypeMap)
    },
    movementTypeChartOptions() {
      const labels = Object.keys(this.movementTypeMap).map(k => TYPE_LABELS[k] || k)
      return this.donutOptions(labels, CHART_COLORS)
    },
    injectionSeries() {
      const yes = this.injectionsCount
      const no = this.visits.length - yes
      return [yes, no]
    },
    injectionChartOptions() {
      return this.donutOptions(['Injection given', 'No injection'], ['#3699FF', '#E4E6EF'])
    },
    topStockItems() {
      return [...this.stock]
        .sort((a, b) => Number(b.stock || 0) - Number(a.stock || 0))
        .slice(0, 10)
    },
    topStockCategories() {
      return this.topStockItems.map(i => this.truncateLabel(i.name, 18))
    },
    topStockSeries() {
      return [{ name: 'Stock', data: this.topStockItems.map(i => Number(i.stock) || 0) }]
    },
    topStockChartOptions() {
      return this.barOptions(this.topStockCategories, false, '#3699FF')
    },
    lowStockItems() {
      return [...this.stockAlerts]
        .sort((a, b) => Number(a.stock || 0) - Number(b.stock || 0))
        .slice(0, 8)
    },
    lowStockCategories() {
      return this.lowStockItems.map(i => this.truncateLabel(i.name, 16))
    },
    lowStockSeries() {
      return [{ name: 'Stock', data: this.lowStockItems.map(i => Number(i.stock) || 0) }]
    },
    lowStockChartOptions() {
      return this.barOptions(this.lowStockCategories, true, '#F64E60')
    },
    visitsByPeriod() {
      const map = {}
      this.visits.forEach(v => {
        const d = v.visit_date ? String(v.visit_date).slice(0, 10) : 'unknown'
        const key = d.length >= 7 ? d.slice(0, 7) : d
        map[key] = (map[key] || 0) + 1
      })
      const keys = Object.keys(map).sort()
      return { keys, values: keys.map(k => map[k]) }
    },
    visitsTimelineCategories() {
      return this.visitsByPeriod.keys
    },
    visitsTimelineSeries() {
      return [{ name: 'Visits', data: this.visitsByPeriod.values }]
    },
    visitsTimelineOptions() {
      return this.lineAreaOptions(this.visitsTimelineCategories, '#3699FF')
    },
    revenueByPeriod() {
      const map = {}
      this.visits.forEach(v => {
        const d = v.visit_date ? String(v.visit_date).slice(0, 10) : ''
        const key = d.length >= 7 ? d.slice(0, 7) : d || 'unknown'
        map[key] = (map[key] || 0) + (Number(v.price_charged) || 0)
      })
      const keys = Object.keys(map).sort()
      return { keys, values: keys.map(k => Math.round(map[k] * 100) / 100) }
    },
    revenueTimelineCategories() {
      return this.revenueByPeriod.keys
    },
    revenueTimelineSeries() {
      return [{ name: 'Revenue', data: this.revenueByPeriod.values }]
    },
    revenueTimelineOptions() {
      return this.lineAreaOptions(this.revenueTimelineCategories, '#1BC5BD')
    },
    movementQtyMap() {
      const map = {}
      this.movements.forEach(m => {
        const t = m.type || 'other'
        map[t] = (map[t] || 0) + (Number(m.qty) || 0)
      })
      return map
    },
    movementQtyCategories() {
      return Object.keys(this.movementQtyMap).map(k => TYPE_LABELS[k] || k)
    },
    movementQtySeries() {
      return [{ name: 'Qty', data: Object.values(this.movementQtyMap) }]
    },
    movementQtyChartOptions() {
      return this.barOptions(this.movementQtyCategories, false, '#8950FC')
    },
    activeTreatments() {
      return this.treatments.filter(t => t.active !== false && t.active !== 0).slice(0, 8)
    },
    treatmentBarCategories() {
      return this.activeTreatments.map(t => this.truncateLabel(t.name, 20))
    },
    treatmentBarSeries() {
      return [{ name: 'Active', data: this.activeTreatments.map(() => 1) }]
    },
    treatmentChartOptions() {
      return {
        ...this.barOptions(this.treatmentBarCategories, true, '#6993FF'),
        plotOptions: { bar: { horizontal: true, distributed: true } },
      }
    },
    recentVisits() {
      return [...this.visits]
        .sort((a, b) => String(b.visit_date || '').localeCompare(String(a.visit_date || '')))
        .slice(0, 8)
        .map(v => ({
          visit_date: v.visit_date ? String(v.visit_date).slice(0, 10) : '—',
          patient: v.patient
            ? `${v.patient.first_name || ''} ${v.patient.last_name || ''}`.trim()
            : `#${v.weightloss_patient_id || '—'}`,
          price_charged: v.price_charged != null ? `$${Number(v.price_charged).toFixed(2)}` : '—',
        }))
    },
    lowStockTable() {
      return this.lowStockItems.map(i => ({
        name: i.name,
        stock: i.stock,
        stock_status: i.stock_status,
      }))
    },
  },
  mounted() {
    this.loadFacilities()
    this.loadAll()
  },
  methods: {
    formatMoney(n) {
      const v = Number(n)
      if (Number.isNaN(v)) return '0'
      return v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v.toFixed(0)
    },
    truncateLabel(s, max) {
      const t = String(s || '')
      return t.length > max ? `${t.slice(0, max)}…` : t
    },
    statusVariant(s) {
      return { ok: 'success', low: 'warning', critical: 'danger', empty: 'secondary' }[s] || 'secondary'
    },
    statusLabel(s) {
      return { ok: 'OK', low: 'Low', critical: 'Critical', empty: 'Empty' }[s] || s
    },
    donutOptions(labels, colors) {
      return {
        chart: { type: 'donut', toolbar: { show: false }, fontFamily: 'inherit' },
        labels,
        colors: colors || CHART_COLORS,
        legend: { position: 'bottom' },
        dataLabels: { enabled: true },
        plotOptions: { pie: { donut: { size: '55%' } } },
      }
    },
    barOptions(categories, horizontal, color) {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        plotOptions: { bar: { horizontal: !!horizontal, borderRadius: 4 } },
        colors: [color || '#3699FF'],
        xaxis: { categories },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f1f1f4' },
      }
    },
    lineAreaOptions(categories, color) {
      return {
        chart: { type: 'area', toolbar: { show: true }, fontFamily: 'inherit', zoom: { enabled: true } },
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05 },
        },
        colors: [color || '#3699FF'],
        xaxis: { categories },
        dataLabels: { enabled: false },
        grid: { borderColor: '#f1f1f4' },
      }
    },
    async loadFacilities() {
      this.facilityOptionsLoading = true
      try {
        const { data } = await axiosF.facilityList(1000)
        this.facilityOptions = (data || []).map(f => ({ id: f.id, name: f.name }))
      } catch {
        this.facilityOptions = []
      } finally {
        this.facilityOptionsLoading = false
      }
    },
    async loadAll() {
      this.loading = true
      this.loadError = ''
      const params = this.baseParams
      try {
        const [treatments, patientsRes, visitsRes, movementsRes, stock, prices, itemsRes] =
          await Promise.all([
            wlTreatmentsApi.list(),
            this.wlPatientsApi.listPaginated(1, params, 1),
            this.wlVisitsApi.listPaginated(500, params, 1),
            invMovementsApi.listPaginated(500, params, 1),
            invStockApi.list(params),
            wlPricesApi.list(),
            invItemsApi.listPaginated(1, params, 1),
          ])
        this.treatments = treatments || []
        this.patientsTotal = patientsRes.total || 0
        this.visits = visitsRes.items || []
        this.movements = movementsRes.items || []
        this.stock = stock || []
        this.prices = prices || []
        this.itemsTotal = itemsRes.total || 0
      } catch (e) {
        this.loadError = e.message || 'Could not load dashboard data'
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.wl-kpi-card {
  padding: 1rem 0.5rem;
}

.wl-kpi-icon {
  font-size: 1.25rem;
}
</style>
